import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  type GitlabProvisioningManageItemStatus,
  type GitlabProvisioningManageResult,
  type GitlabProvisioningProvisionResult,
} from 'network/httpClients/GitlabClient';
import { useGitlabProvisionMutation } from 'network/mutations/useGitlabProvisionMutation';
import {
  isTerminalProvisioningStatus,
  useGitlabProvisionStatusQuery,
} from 'network/queries/useGitlabProvisionStatusQuery';
import { ROUTES } from 'routes/consts';
import { InstallationStatusMessage, Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { spawn } from 'utils/translations';
import { GENERIC_PROVISIONING_ERROR_KEY } from '../consts';
import { useGitlabInstallationV2 } from '../gitlabInstallationV2Context';
import {
  computeMultiGroupOutcome,
  getResultLabel,
  getSucceededResults,
  ScopeResultList,
  type ManageRunOutcome,
} from '../manageRun';
import { partitionSelectionByTopLevelGroup } from '../selectionUtils';
import { GITLAB_V2_HOST, type GitlabV2GroupInstallPartition } from '../types';
import {
  getInstallErrorKey,
  getProvisionErrorKey,
  getTransportErrorKey,
} from '../utils';
import {
  GroupInstallRunner,
  type GroupInstallReport,
} from './GroupInstallRunner';

/** Optimistic rows for one group's scopes, in a given (transient) status. */
const seedGroupRows = (
  add: GitlabV2GroupInstallPartition['add'],
  status: GitlabProvisioningManageItemStatus,
): GitlabProvisioningManageResult[] => [
  ...add.groupIds.map((id) => ({
    scope: String(id),
    kind: 'group' as const,
    status,
    error: null,
  })),
  ...add.projectIds.map((id) => ({
    scope: String(id),
    kind: 'project' as const,
    status,
    error: null,
  })),
];

/** One top-level group's install data: its scopes' rows, plus a reason set once when the
 * whole group's installation couldn't be provisioned (surfaced above the flat list). */
type InstallSection = {
  topLevelGroupId: number;
  groupPath: string;
  rows: GitlabProvisioningManageResult[];
  provisionErrorReason: string | undefined;
};

/**
 * Multi-group Install step (cloud host-only) — provisioning was deferred from Connect, so
 * on entry it partitions the Select selection by top-level group, fires ONE batch
 * `provision({ topLevelGroupIds })`, polls the per-group `results`, then installs each
 * group's scopes via a headless `GroupInstallRunner` per installation. Every scope across
 * all groups renders in one flat list with its full path and a group/repo icon. A group
 * whose bot can't be created is called out once above the list (its scopes still read
 * "Failed"). One overall banner + progress + Retry (re-runs the whole idempotent batch)
 * span all groups. A note explains each group becomes its own item on the installations
 * screen. Back is blocked; a lost session restarts the wizard.
 */
export const MultiGroupInstallResults = () => {
  const { setIsNextEnabled, setIsPending } = useMultiStepDialog();
  const { accessToken, gitlabUrl, installSelection, scopePaths } =
    useGitlabInstallationV2();

  const spawnInstall = spawn('dialogs.gitlab-installation-v2.steps.install');
  const spawnErrors = spawn(
    'dialogs.gitlab-installation-v2.steps.install.errors',
  );
  const spawnItemStatus = spawn(
    'dialogs.gitlab-installation-v2.steps.install.item-status',
  );
  const spawnProvisionErrors = spawn(
    'dialogs.gitlab-installation-v2.steps.provision.errors',
  );

  const canStart =
    accessToken !== undefined &&
    gitlabUrl !== '' &&
    installSelection !== undefined;

  const partition = useMemo(
    () =>
      installSelection
        ? partitionSelectionByTopLevelGroup(installSelection, scopePaths)
        : [],
    [installSelection, scopePaths],
  );

  const { mutate: startProvision } = useGitlabProvisionMutation();
  const [provisionId, setProvisionId] = useState<string | undefined>();
  const [provisionStartErrorKey, setProvisionStartErrorKey] = useState<
    string | undefined
  >();
  const [reportByGroup, setReportByGroup] = useState<
    Record<number, GroupInstallReport>
  >({});
  const hasStartedRef = useRef(false);

  const {
    data: provisionStatus,
    isError: isProvisionStatusError,
    error: provisionStatusError,
  } = useGitlabProvisionStatusQuery(provisionId);

  const startProvisioning = useCallback(() => {
    if (accessToken === undefined || gitlabUrl === '' || partition.length === 0)
      return;

    setProvisionStartErrorKey(undefined);
    setProvisionId(undefined);
    setReportByGroup({});
    startProvision(
      {
        gitlabUrl,
        accessToken,
        topLevelGroupIds: partition.map((entry) => entry.topLevelGroupId),
      },
      {
        onSuccess: (provision) => setProvisionId(provision.provisionId),
        onError: () =>
          setProvisionStartErrorKey(GENERIC_PROVISIONING_ERROR_KEY),
      },
    );
  }, [accessToken, gitlabUrl, partition, startProvision]);

  useEffect(() => {
    if (hasStartedRef.current || !canStart || partition.length === 0) return;

    hasStartedRef.current = true;
    startProvisioning();
  }, [canStart, partition, startProvisioning]);

  const onGroupReport = useCallback(
    (topLevelGroupId: number, report: GroupInstallReport) => {
      setReportByGroup((prev) => {
        const current = prev[topLevelGroupId];

        if (
          current &&
          current.results === report.results &&
          current.outcome === report.outcome
        )
          return prev;

        return { ...prev, [topLevelGroupId]: report };
      });
    },
    [],
  );

  const resultByGroupId = useMemo(() => {
    const map = new Map<number, GitlabProvisioningProvisionResult>();

    provisionStatus?.results.forEach((result) => {
      // Multi-group cloud results always carry a numeric id; null is self-managed,
      // which never reaches this view.
      if (result.topLevelGroupId !== null) {
        map.set(result.topLevelGroupId, result);
      }
    });

    return map;
  }, [provisionStatus]);

  // A non-empty selection that partitions to nothing (a scope whose top-level group id
  // couldn't be resolved — a BE contract violation) would otherwise never provision and
  // hang on pending; surface it as a terminal error instead.
  const hasSelection =
    installSelection !== undefined &&
    installSelection.groupIds.length + installSelection.projectIds.length > 0;
  const partitionUnresolved = hasSelection && partition.length === 0;

  const hasWholeRunError =
    provisionStartErrorKey !== undefined ||
    isProvisionStatusError ||
    partitionUnresolved;

  // Once polling has stopped (overall status terminal), a requested group still absent
  // from results[] can never be reported — treat it as a terminal failure, not a hang.
  const isProvisionTerminal = isTerminalProvisioningStatus(
    provisionStatus?.status,
  );

  // Per top-level group (each is its own installation): rows come from that group's runner
  // once provisioned, seeded "waiting" while provisioning, or seeded "failed" if the
  // group's bot couldn't be created — in which case the reason is surfaced once above the
  // flat list. Ordered by top-level group; all rows are concatenated into one list.
  const sections: InstallSection[] = partition.map((entry) => {
    const provisionResult = resultByGroupId.get(entry.topLevelGroupId);
    const reported = reportByGroup[entry.topLevelGroupId];

    if (provisionResult?.status === 'done' && provisionResult.installationId) {
      const rows =
        reported && reported.results.length > 0
          ? reported.results
          : seedGroupRows(
              entry.add,
              reported?.outcome === 'failed' ? 'failed' : 'queued',
            );

      return {
        topLevelGroupId: entry.topLevelGroupId,
        groupPath: entry.groupPath,
        rows,
        provisionErrorReason: undefined,
      };
    }

    // Failed, `done` with no installation id, or absent from results[] once polling has
    // stopped (all contract violations — this group can never reach manage): show its
    // scopes failed so the run reaches a terminal state instead of hanging on "waiting".
    const isProvisionFailed =
      provisionResult?.status === 'failed' ||
      (provisionResult?.status === 'done' && !provisionResult.installationId) ||
      (provisionResult === undefined && isProvisionTerminal);

    return {
      topLevelGroupId: entry.topLevelGroupId,
      groupPath: entry.groupPath,
      rows: seedGroupRows(entry.add, isProvisionFailed ? 'failed' : 'queued'),
      provisionErrorReason: isProvisionFailed
        ? spawnProvisionErrors(
            getProvisionErrorKey(
              provisionResult?.error?.code,
              GITLAB_V2_HOST.CLOUD,
            ),
          )
        : undefined,
    };
  });

  const results = sections.flatMap((section) => section.rows);
  const provisionFailures = sections.filter(
    (section): section is InstallSection & { provisionErrorReason: string } =>
      section.provisionErrorReason !== undefined,
  );

  const perGroupOutcome = (
    entry: GitlabV2GroupInstallPartition,
  ): ManageRunOutcome => {
    const provisionResult = resultByGroupId.get(entry.topLevelGroupId);

    // Absent from results[]: still pending while polling continues, but a terminal
    // overall status means it will never arrive — fail it so the run can settle.
    if (provisionResult === undefined)
      return isProvisionTerminal ? 'failed' : 'pending';

    if (provisionResult.status === 'in_progress') return 'pending';

    if (provisionResult.status === 'failed') return 'failed';

    // `done` without an installation id can't proceed to manage — terminal failure,
    // not a perpetual pending (polling has already stopped on overall `done`).
    if (!provisionResult.installationId) return 'failed';

    return reportByGroup[entry.topLevelGroupId]?.outcome ?? 'pending';
  };

  const overallOutcome: ManageRunOutcome = hasWholeRunError
    ? 'failed'
    : computeMultiGroupOutcome(partition.map(perGroupOutcome));

  const isPending = overallOutcome === 'pending';
  const canProceed =
    overallOutcome === 'success' || overallOutcome === 'partial';
  // Retry re-fires provisioning; it can't help an unresolvable partition, so hide it there.
  const canRetry =
    (overallOutcome === 'failed' || overallOutcome === 'partial') &&
    !partitionUnresolved;

  useEffect(() => {
    setIsNextEnabled(canProceed);
  }, [canProceed]);

  useEffect(() => {
    setIsPending(isPending);
  }, [isPending]);

  // A refresh or deep-link onto this step loses the in-memory wizard context (PAT,
  // selection); the PAT can't be recovered, so restart the wizard.
  if (
    accessToken === undefined ||
    gitlabUrl === '' ||
    installSelection === undefined
  ) {
    return (
      <Navigate
        to={{
          pathname: ROUTES.GITLAB_V2_CONNECT,
          search: window.location.search,
        }}
        replace
      />
    );
  }

  // A per-scope webhook failure carries an install code; a whole-group provisioning
  // failure shows its reason once above the list, so those rows read plain "Failed".
  const itemStatusLabel = (result: GitlabProvisioningManageResult): string =>
    result.status === 'failed' && result.error
      ? spawnErrors(getInstallErrorKey(result.error.code))
      : spawnItemStatus(result.status);

  const bannerStatus =
    overallOutcome === 'success'
      ? 'success'
      : overallOutcome === 'partial'
        ? 'warning'
        : 'error';

  const getBannerMessage = () => {
    if (hasWholeRunError) {
      return provisionStartErrorKey
        ? spawnProvisionErrors(provisionStartErrorKey)
        : spawnProvisionErrors(getTransportErrorKey(provisionStatusError, 404));
    }

    if (overallOutcome === 'success') return spawnInstall('done');

    if (overallOutcome === 'partial') {
      return spawnInstall('partial', {
        success: getSucceededResults(results).length,
        total: results.length,
      });
    }

    return spawnInstall('all-failed');
  };

  const groupCount = installSelection.groupIds.length;
  const projectCount = installSelection.projectIds.length;
  const summaryParts = [
    groupCount > 0
      ? spawnInstall('summary-groups', { count: groupCount })
      : undefined,
    projectCount > 0
      ? spawnInstall('summary-projects', { count: projectCount })
      : undefined,
  ].filter((part): part is string => part !== undefined);
  const pendingDescription =
    summaryParts.length > 0
      ? spawnInstall('summary', {
          scopes: summaryParts.join(spawnInstall('summary-join')),
        })
      : spawnInstall('description');

  const resultLabel = (result: GitlabProvisioningManageResult) =>
    getResultLabel(
      result,
      scopePaths.groupPathsById,
      scopePaths.projectPathsById,
    );

  return (
    <Step title={spawnInstall('title')}>
      {partition.map((entry) => {
        const provisionResult = resultByGroupId.get(entry.topLevelGroupId);

        if (
          provisionResult?.status !== 'done' ||
          !provisionResult.installationId
        )
          return null;

        return (
          <GroupInstallRunner
            key={entry.topLevelGroupId}
            partition={entry}
            installationId={provisionResult.installationId}
            accessToken={accessToken}
            onReport={onGroupReport}
          />
        );
      })}

      <div className="flex flex-col gap-4">
        {sections.length > 0 && (
          <p className="text-sm text-text-general-neutral-mild">
            {spawnInstall('multi-group-note')}
          </p>
        )}

        {isPending ? (
          <InstallationStatusMessage
            status="pending"
            message={pendingDescription}
          />
        ) : (
          <InstallationStatusMessage
            status={bannerStatus}
            message={getBannerMessage()}
            retryLabel={canRetry ? spawnInstall('retry-label') : undefined}
            onRetry={canRetry ? startProvisioning : undefined}
          />
        )}

        {provisionFailures.length > 0 && (
          <div className="flex flex-col gap-1">
            {provisionFailures.map((section) => (
              <span
                key={section.topLevelGroupId}
                className="text-sm text-text-tag-negative-active"
              >
                {spawnInstall('group-provision-failed', {
                  group: section.groupPath,
                  reason: section.provisionErrorReason,
                })}
              </span>
            ))}
          </div>
        )}

        {results.length > 0 && (
          <ScopeResultList
            results={results}
            showKindIcon
            getResultStatusLabel={itemStatusLabel}
            getResultLabel={resultLabel}
          />
        )}
      </div>
    </Step>
  );
};
